/**
 * Shortens a column title for the dense mobile table.
 *
 * At 88px a column fits roughly ten characters, and the filter configs carry 512
 * distinct titles — "Total Loan Balance", "Days Sales Outstanding", "Avg. Outstanding
 * Ownership (%)". Truncating them with an ellipsis produces a header row of
 * "TOTAL LOAN …" / "DAYS SALES …" that distinguishes nothing, which is the whole
 * problem: on a dense table the header is the only thing telling you what a number is.
 *
 * So this abbreviates rather than truncates, and it is a pipeline rather than a lookup
 * table on purpose — a 512-entry hand-written map would be stale the day someone adds a
 * column, and the useful abbreviations are compositional ("Total X" -> "TOT X") rather
 * than per-title. `shortTitle` on IFilter is the escape hatch for the cases where the
 * pipeline reads badly.
 *
 * Lives in `common/` because the filter configs it serves are there, and the server's
 * CSV/XLSX export headers can reuse it.
 */
import escapeRegExp from 'lodash/escapeRegExp'

/**
 * Whole-title overrides, for titles where no amount of word-level substitution gives a
 * good answer. Matched case-insensitively on the normalized title.
 */
export const TITLE_ABBREVIATIONS: Readonly<Record<string, string>> = {
  'days sales outstanding': 'DSO',
  'accounts receivable': 'AR',
  'accounts payable': 'AP',
  'borrowing base': 'BB',
  'due diligence': 'DD',
  'company / organization': 'COMPANY',
  'relationship to company': 'RELATION',
  'not shared with clients': 'INTERNAL',
  'shared with clients': 'SHARED',
  'corporate guarantees and pledges': 'GUARANTEES',
  'subordination agreements': 'SUBORD',
  'watchlist & workout tiers': 'TIER',
  'collections & adjustments': 'COLL/ADJ',
  'fundings & pass-throughs': 'FUND/PT',
  'term sheet negotiation': 'TERM SHT',
  'authorized signature': 'SIGNATURE',
  'months of sales count': 'MOS SALES',
  'entity prediction': 'PREDICTION',
  'interest override': 'INT OVRD',
}

/**
 * Multi-word phrases, applied longest-first so "Advance Rate" wins over "Rate".
 * Order within the array does not matter — the sort below handles precedence — but
 * keeping them grouped by domain makes the list reviewable.
 */
export const PHRASE_ABBREVIATIONS: ReadonlyArray<readonly [string, string]> = [
  ['year over year', 'YOY'],
  ['purchase order', 'PO'],
  ['advance rate', 'ADV RT'],
  ['loan balance', 'BAL'],
  ['term loan', 'TERM'],
  ['general ledger', 'GL'],
  ['field exam', 'EXAM'],
  ['cap table', 'CAP'],
  ['risk rating', 'RISK'],
  ['excess availability', 'EXCESS AVL'],
  ['dilutive credits', 'DIL CR'],
  ['effective balance', 'EFF BAL'],
  ['customer deposits', 'CUST DEP'],
  ['finished goods', 'FG'],
  ['last reported', 'REPORTED'],
]

/** Single-word substitutions. Applied after the phrase pass. */
export const WORD_ABBREVIATIONS: Readonly<Record<string, string>> = {
  amount: 'AMT',
  balance: 'BAL',
  average: 'AVG',
  avg: 'AVG',
  outstanding: 'OUTST',
  ownership: 'OWN',
  receivable: 'AR',
  receivables: 'AR',
  payable: 'AP',
  payables: 'AP',
  description: 'DESC',
  customer: 'CUST',
  customers: 'CUST',
  vendor: 'VEND',
  vendors: 'VEND',
  invoice: 'INV',
  invoices: 'INV',
  inventory: 'INVT',
  ineligible: 'INELIG',
  eligible: 'ELIG',
  collections: 'COLL',
  collection: 'COLL',
  category: 'CAT',
  total: 'TOT',
  percent: '%',
  percentage: '%',
  number: '#',
  quantity: 'QTY',
  account: 'ACCT',
  available: 'AVL',
  availability: 'AVL',
  collateral: 'COLL',
  concentration: 'CONC',
  payment: 'PMT',
  payments: 'PMT',
  interest: 'INT',
  principal: 'PRIN',
  reserve: 'RSV',
  reserves: 'RSV',
  adjusted: 'ADJ',
  adjustment: 'ADJ',
  adjustments: 'ADJ',
  weighted: 'WTD',
  cumulative: 'CUM',
  transaction: 'TXN',
  transactions: 'TXN',
  organization: 'ORG',
  company: 'CO',
  frequency: 'FREQ',
  reporting: 'RPT',
  difference: 'DIFF',
  beginning: 'BEG',
  ending: 'END',
  current: 'CUR',
  previous: 'PREV',
  diluted: 'DIL',
  dilution: 'DIL',
  exposure: 'EXP',
  participant: 'PART',
  participants: 'PART',
  prediction: 'PRED',
  commitment: 'COMMIT',
  contractual: 'CONTR',
  requests: 'REQS',
  request: 'REQ',
  documents: 'DOCS',
  document: 'DOC',
  industry: 'IND',
  location: 'LOC',
  business: 'BIZ',
  estimated: 'EST',
  actual: 'ACT',
  implied: 'IMPL',
  projected: 'PROJ',
  original: 'ORIG',
  remaining: 'REM',
  maximum: 'MAX',
  minimum: 'MIN',
  // Long words drawn from the real corpus. Every entry here is a word the
  // last-resort truncation would otherwise mangle — "QUICKBOO", "THRESHO",
  // "GROS MARGI" — so the dictionary is what keeps labels readable, and this is the
  // list to extend when a new column reads badly.
  standard: 'STD',
  activity: 'ACTVTY',
  completed: 'DONE',
  finished: 'FIN',
  investment: 'INVMT',
  submitted: 'SUBMIT',
  appeared: 'SEEN',
  confidence: 'CONF',
  department: 'DEPT',
  deposits: 'DEP',
  deposit: 'DEP',
  diligence: 'DILIG',
  dilutive: 'DIL',
  effective: 'EFF',
  expiration: 'EXPIRY',
  facility: 'FAC',
  financials: 'FIN',
  fundings: 'FUND',
  funding: 'FUND',
  guarantees: 'GTEE',
  guarantee: 'GTEE',
  headline: 'HEAD',
  override: 'OVRD',
  potential: 'POTNL',
  priority: 'PRIO',
  processed: 'PROC',
  received: 'RCVD',
  relationship: 'RELTN',
  returned: 'RTND',
  'sub-type': 'SUBTYPE',
  warehouse: 'WHSE',
  watchlist: 'WATCH',
  accounts: 'ACCT',
  agreements: 'AGMTS',
  agreement: 'AGMT',
  amendment: 'AMEND',
  amortization: 'AMORT',
  analysis: 'ANLYS',
  archived: 'ARCHV',
  assigned: 'ASSGN',
  assignee: 'ASSGN',
  authorized: 'AUTH',
  candidates: 'CANDS',
  certificate: 'CERT',
  threshold: 'THRESH',
  quickbooks: 'QB',
  margin: 'MARGIN',
  gross: 'GROSS',
  applied: 'APPL',
  required: 'REQD',
  connected: 'CONN',
  accrued: 'ACCR',
  aging: 'AGING',
  reports: 'RPTS',
  report: 'RPT',
  utilization: 'UTIL',
  utilized: 'UTIL',
  turn: 'TURN',
  advance: 'ADV',
  ineligibles: 'INELIG',
  // Second curation pass, driven by the corpus test after the budget dropped to 10.
  // Each of these was being shaved into a fragment — SUMMAR, FOREIG, PRIMAR, COMPLE.
  summary: 'SUMM',
  changes: 'CHG',
  change: 'CHG',
  status: 'STAT',
  foreign: 'FGN',
  country: 'CTRY',
  primary: 'PRIM',
  record: 'REC',
  related: 'REL',
  companies: 'COS',
  consent: 'CNST',
  board: 'BRD',
  member: 'MBR',
  members: 'MBRS',
  seats: 'SEATS',
  seat: 'SEAT',
  credit: 'CR',
  credits: 'CR',
  limit: 'LMT',
  weeks: 'WKS',
  supply: 'SUP',
  value: 'VAL',
  months: 'MOS',
  revenue: 'REV',
  attention: 'ATTN',
  negative: 'NEG',
  waivers: 'WVR',
  waiver: 'WVR',
  filings: 'FILE',
  filing: 'FILE',
  notice: 'NOTE',
  support: 'SUPP',
  holder: 'HLDR',
  holders: 'HLDRS',
  digits: 'DIG',
  target: 'TGT',
  complete: 'COMPL',
  contract: 'CONTR',
  updated: 'UPD',
  closing: 'CLOSE',
  termination: 'TERM',
  duplicate: 'DUP',
  duplicates: 'DUPS',
  'pass-through': 'PT',
  concentr: 'CONC',
  conc: 'CONC',
  act: 'ACT',
  est: 'EST',
  retrieved: 'RETR',
  unresolved: 'UNRES',
  unmapped: 'UNMAP',
  predicted: 'PRED',
  reminders: 'REMIND',
  maturity: 'MAT',
  collaborating: 'COLLAB',
  intermediary: 'INTMED',
  starting: 'START',
}

/** Dropped entirely — they carry no distinguishing information in a 10-char header. */
const STOPWORDS = new Set([
  'of',
  'the',
  'and',
  'for',
  'in',
  'on',
  'at',
  'to',
  'by',
  'a',
  'an',
  'with',
  'from',
])

/**
 * Characters that fit a header cell in the dense layout.
 *
 * Measured in the running app rather than in a harness: an 88px cell leaves 76px usable
 * after padding, and the header renders at 9px uppercase. At that size "UTILIZATION"
 * (11 chars) lays out at 78px and truncates, while "LINE TURN" (9 chars) fits at 65px —
 * so the usable budget is about ten characters.
 *
 * This was 13 for a while, taken from a standalone harness that rendered at 10px with
 * different padding. Close enough to look right, wrong enough to truncate real headers.
 * If the dense font size or column width changes, re-measure rather than re-reason.
 */
const DEFAULT_MAX_LENGTH = 10

/**
 * Collapses whitespace and lowercases, so lookups do not care about source formatting.
 *
 * `(s)` is folded into the word it belongs to — "Holder(s)" becomes "holders" — because
 * the punctuation strip later would otherwise leave a bare "s" as its own token, and
 * "BOARD SEAT S" reads as a mistake.
 */
const normalize = (value: string): string =>
  value.trim().replace(/\(s\)/gi, 's').replace(/\s+/g, ' ').toLowerCase()

// Longest first, so a phrase is never pre-empted by a shorter phrase inside it.
const SORTED_PHRASES = [...PHRASE_ABBREVIATIONS].sort((a, b) => b[0].length - a[0].length)

const joinLength = (tokens: string[]): number => tokens.join(' ').length

/**
 * Drops the most central token, repeatedly, until the label fits.
 *
 * The only length-reduction step, and it never invents a word: the first and last tokens
 * carry the most meaning, so "Total Loan Balance" becomes "TOT BAL" rather than "TOT
 * LOAN". Never reduces below two tokens.
 *
 * There used to be a character-shaving pass here as well, trimming the longest token a
 * letter at a time. It is gone: shaving is the only step that can turn a word into a
 * fragment, and "SUMMAR CHANGE" / "FOREIG COUNTR" / "PRIMAR RECORD" read as typos where an
 * ellipsis reads as truncation. A label that still exceeds the budget is left alone for the
 * cell to ellipsis, with the full title in the tooltip. The fix for a specific bad one is a
 * WORD_ABBREVIATIONS entry or a `shortTitle`.
 */
const trimMiddleOut = (tokens: string[], maxLength: number): string[] => {
  const result = [...tokens]
  while (joinLength(result) > maxLength && result.length > 2) {
    result.splice(Math.floor(result.length / 2), 1)
  }
  return result
}

// 512 distinct titles, re-resolved on every header render — worth a cache, and the
// function is pure so the cache can never be stale.
const cache = new Map<string, string>()

/**
 * Returns the dense-table label for a column title.
 *
 * Empty string for a missing title, so a caller can treat "no title" and "abbreviates to
 * nothing" the same way — an untitled column (an expand chevron, an actions column) has
 * no header to show.
 */
export const abbreviateTitle = (
  title?: string | null,
  maxLength: number = DEFAULT_MAX_LENGTH,
): string => {
  if (!title) {
    return ''
  }

  const cacheKey = `${maxLength}:${title}`
  const cached = cache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }

  const resolve = (): string => {
    const normalized = normalize(title)

    const exact = TITLE_ABBREVIATIONS[normalized]
    if (exact) {
      return exact
    }

    // Already short enough to leave alone — "Date", "Type", "AR". Uppercased for
    // consistency with the abbreviated ones, since they share a header row.
    if (normalized.length <= 6) {
      return normalized.toUpperCase()
    }

    // Already an emitted label: no lowercase letters and inside the budget. Makes the
    // function idempotent, which matters because two different call sites can abbreviate
    // — TableFiltersRow for config-driven columns, TableCell for hand-written header
    // cells — and a label run through twice would be shortened twice.
    if (title === title.toUpperCase() && title.trim().length <= maxLength) {
      return title.trim()
    }

    let working = normalized
    SORTED_PHRASES.forEach(([phrase, replacement]) => {
      working = working.replace(new RegExp(`\\b${escapeRegExp(phrase)}\\b`, 'g'), replacement)
    })

    const tokens = working
      // Punctuation carries no meaning here, except `%`, `#` and `$`, which carry a lot.
      .replace(/[()[\]{},;:"'.]/g, ' ')
      .split(/[\s/]+/)
      .filter(Boolean)
      .filter((token) => !STOPWORDS.has(token))
      .map((token) => {
        // A four-digit year becomes two digits: period columns read "JAN 2022" and the
        // century is never the thing distinguishing one column from the next. Worth two
        // characters in a header that only has ten.
        if (/^(19|20)\d{2}$/.test(token)) {
          return token.slice(2)
        }

        const word = WORD_ABBREVIATIONS[token]
        if (word) {
          return word
        }
        // A token that is already an abbreviation or a symbol (AR, GL, %, 91+, L3M)
        // survives untouched; anything else is just uppercased.
        return token.toUpperCase()
      })
      // A word map can substitute `%`, which may now duplicate a literal `%`.
      .filter((token, index, all) => token !== all[index - 1])

    if (tokens.length === 0) {
      return normalized.toUpperCase()
    }

    // Dictionary first (already applied above), then drop redundant words. Nothing is
    // cut mid-word; anything still over budget is ellipsed by the cell.
    return trimMiddleOut(tokens, maxLength).join(' ')
  }

  const resolved = resolve()
  cache.set(cacheKey, resolved)
  return resolved
}

export default abbreviateTitle
